import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost } from '../../utils/api';

const StayOverview = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('rooms'); // 'rooms' or 'guests'
  const [rooms, setRooms] = useState([]);
  const [roomsByFloor, setRoomsByFloor] = useState({});
  const [currentGuests, setCurrentGuests] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showGuestDetails, setShowGuestDetails] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [checkoutGuest, setCheckoutGuest] = useState(null);

  // Form state for guest assignment
  const [guestForm, setGuestForm] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    guestNationality: 'Indian',
    bookingType: 'Single',
    numberOfGuests: 1,
    guestIdProof: '',
    checkInDate: new Date().toISOString().split('T')[0],
    checkOutDate: '',
    totalAmount: ''
  });

  // Color palette
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    available: '#10b981',
    occupied: '#ef4444',
    maintenance: '#f59e0b'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;
  const isDesktop = windowWidth > 1024;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRooms(),
        fetchCurrentGuests(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchRooms = async () => {
    try {
      const data = await apiGet('/admin/stay-overview/rooms');
      if (data.success) {
        setRooms(data.data.rooms);
        setRoomsByFloor(data.data.roomsByFloor);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchCurrentGuests = async () => {
    try {
      const data = await apiGet('/admin/stay-overview/current-guests');
      if (data.success) {
        setCurrentGuests(data.data);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/stay-overview/stats');
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleRoomClick = async (room) => {
    setSelectedRoom(room);
    if (room.status === 'occupied' && room.booking_id) {
      // Fetch detailed room info
      try {
        const data = await apiGet(`/admin/stay-overview/room/${room.id}`);
        if (data.success) {
          setSelectedRoom(data.data.room);
          setShowGuestDetails(true);
        }
      } catch (error) {
        console.error('Error fetching room details:', error);
      }
    } else if (room.status === 'available') {
      setShowAssignForm(true);
      // Set default total amount based on room price
      setGuestForm(prev => ({
        ...prev,
        totalAmount: room.price_per_night
      }));
    }
  };

  const handleAssignGuest = async (e) => {
    e.preventDefault();

    try {
      const data = await apiPost('/admin/stay-overview/assign-guest', {
        roomId: selectedRoom.id,
        ...guestForm
      });

      if (data.success) {
        alert('Guest assigned successfully!');
        setShowAssignForm(false);
        resetGuestForm();
        fetchAllData();
      } else {
        alert(data.message || 'Error assigning guest');
      }
    } catch (error) {
      console.error('Error assigning guest:', error);
      alert('Error assigning guest');
    }
  };

  const handleCheckout = async (guest) => {
    setCheckoutGuest(guest);
    setShowCheckoutConfirm(true);
  };

  const confirmCheckout = async () => {
    try {
      const data = await apiPost(`/admin/stay-overview/checkout/${checkoutGuest.id}`, {});

      if (data.success) {
        alert('Guest checked out successfully!');
        setShowCheckoutConfirm(false);
        setCheckoutGuest(null);
        fetchAllData();
      } else {
        alert(data.message || 'Error during checkout');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      alert('Error during checkout');
    }
  };

  const resetGuestForm = () => {
    setGuestForm({
      guestName: '',
      guestEmail: '',
      guestPhone: '',
      guestNationality: 'Indian',
      bookingType: 'Single',
      numberOfGuests: 1,
      guestIdProof: '',
      checkInDate: new Date().toISOString().split('T')[0],
      checkOutDate: '',
      totalAmount: ''
    });
  };



  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return colors.available;
      case 'occupied': return colors.occupied;
      case 'maintenance': return colors.maintenance;
      default: return colors.textLight;
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    headerContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '12px' : '16px'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px',
      lineHeight: '1.2'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
      color: colors.textLight,
      fontWeight: '400',
      lineHeight: '1.4'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : isTablet
          ? 'repeat(2, 1fr)'
          : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : isTablet ? '16px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '16px' : isTablet ? '18px' : '20px',
      borderRadius: isMobile ? '12px' : '14px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      transition: 'transform 0.3s ease'
    },
    statValue: {
      fontSize: isMobile ? '24px' : isTablet ? '28px' : '32px',
      fontWeight: '800',
      marginBottom: '6px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600',
      letterSpacing: '0.5px'
    },
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      borderBottom: `2px solid ${colors.mediumGray}`,
      flexWrap: 'wrap'
    },
    tab: {
      padding: isMobile ? '10px 16px' : '12px 24px',
      background: 'transparent',
      border: 'none',
      borderBottom: '3px solid transparent',
      cursor: 'pointer',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      color: colors.textLight,
      transition: 'all 0.3s ease',
      marginBottom: '-2px'
    },
    activeTab: {
      color: colors.primary,
      borderBottom: `3px solid ${colors.primary}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : isTablet ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    legendContainer: {
      display: 'flex',
      gap: isMobile ? '12px' : '16px',
      marginBottom: isMobile ? '16px' : '20px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'flex-start' : 'center'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '600'
    },
    legendDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%'
    },
    floorSection: {
      marginBottom: isMobile ? '20px' : '24px'
    },
    floorTitle: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '10px' : '12px',
      padding: isMobile ? '8px 12px' : '10px 16px',
      background: colors.lightGray,
      borderRadius: '8px',
      display: 'inline-block'
    },
    roomsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? 'repeat(2, 1fr)'
        : isTablet
          ? 'repeat(4, 1fr)'
          : 'repeat(6, 1fr)',
      gap: isMobile ? '10px' : isTablet ? '12px' : '14px',
      marginTop: isMobile ? '12px' : '16px'
    },
    roomCard: {
      aspectRatio: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: isMobile ? '10px' : '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid',
      position: 'relative'
    },
    roomNumber: {
      fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
      fontWeight: '800',
      color: colors.white,
      marginBottom: '4px'
    },
    roomType: {
      fontSize: isMobile ? '9px' : '10px',
      color: colors.white,
      opacity: 0.9,
      textAlign: 'center',
      fontWeight: '600'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : isTablet ? '28px' : '32px',
      maxWidth: isMobile ? '100%' : isTablet ? '500px' : '600px',
      width: '100%',
      maxHeight: isMobile ? '90vh' : '85vh',
      overflowY: 'auto',
      position: 'relative'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '16px' : '20px',
      paddingBottom: isMobile ? '12px' : '16px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    modalTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: colors.textLight,
      padding: '4px',
      lineHeight: 1
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: isMobile ? '10px 0' : '12px 0',
      borderBottom: `1px solid ${colors.lightGray}`,
      fontSize: isMobile ? '13px' : '14px'
    },
    detailLabel: {
      fontWeight: '600',
      color: colors.textLight
    },
    detailValue: {
      fontWeight: '600',
      color: colors.textDark,
      textAlign: 'right'
    },
    formGroup: {
      marginBottom: isMobile ? '14px' : '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px 12px' : '12px 14px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '14px',
      fontFamily: 'inherit',
      transition: 'border-color 0.3s ease'
    },
    select: {
      width: '100%',
      padding: isMobile ? '10px 12px' : '12px 14px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '14px',
      fontFamily: 'inherit',
      background: colors.white,
      cursor: 'pointer'
    },
    button: {
      padding: isMobile ? '10px 20px' : '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%'
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white
    },
    dangerButton: {
      background: colors.danger,
      color: colors.white
    },
    secondaryButton: {
      background: colors.mediumGray,
      color: colors.textDark
    },
    guestTable: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px'
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    badge: {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      display: 'inline-block'
    },
    successBadge: {
      background: 'rgba(74, 124, 42, 0.15)',
      color: colors.success
    },
    warningBadge: {
      background: 'rgba(245, 158, 11, 0.15)',
      color: colors.warning
    },
    dangerBadge: {
      background: 'rgba(239, 68, 68, 0.15)',
      color: colors.danger
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading stay overview...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.headerContainer}>
          <div style={{ flex: 1 }}>
            <h1 style={styles.pageTitle}>Stay Overview</h1>
            <p style={styles.pageSubtitle}>
              {isMobile
                ? 'Monitor guest stays and occupancy'
                : 'Comprehensive overview of guest stays, occupancy rates, and stay management'}
            </p>
          </div>

          {/* Navigation to 6-Month Forecast */}
          <button
            onClick={() => window.location.href = '/admin/availability-forecast'}
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
              color: colors.white,
              padding: isMobile ? '12px 20px' : '14px 28px',
              borderRadius: '12px',
              border: 'none',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(45, 80, 22, 0.25)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: isMobile ? '100%' : 'auto',
              marginTop: isMobile ? '12px' : '0',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(45, 80, 22, 0.35)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 80, 22, 0.25)';
              }
            }}
          >
            <span style={{ fontSize: '16px' }}>�</span>
            <span>View 6-Month Forecast</span>
          </button>
        </div>
      </div>

      {/* Statistics Grid */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            {stats.totalRooms || 0}
          </div>
          <div style={styles.statLabel}>Total Rooms</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.available}` }}>
          <div style={{ ...styles.statValue, color: colors.available }}>
            {stats.availableRooms || 0}
          </div>
          <div style={styles.statLabel}>Available</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.occupied}` }}>
          <div style={{ ...styles.statValue, color: colors.occupied }}>
            {stats.occupiedRooms || 0}
          </div>
          <div style={styles.statLabel}>Occupied</div>
        </div>

        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ ...styles.statValue, color: colors.accent }}>
            🇮🇳 {stats.indianGuestsCount || 0} / 🌍 {stats.foreignGuestsCount || 0}
          </div>
          <div style={styles.statLabel}>Indian / Foreign</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'rooms' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('rooms')}
        >
          🏨 Rooms
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'guests' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('guests')}
        >
          👥 Current Guests ({currentGuests.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'rooms' ? (
        <div style={styles.contentCard}>
          {/* Legend */}
          <div style={styles.legendContainer}>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: colors.available }}></div>
              <span>Available</span>
            </div>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: colors.occupied }}></div>
              <span>Occupied</span>
            </div>
            <div style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: colors.maintenance }}></div>
              <span>Maintenance</span>
            </div>
          </div>

          {/* Rooms by Floor */}
          {Object.keys(roomsByFloor).sort((a, b) => b - a).map(floor => (
            <div key={floor} style={styles.floorSection}>
              <div style={styles.floorTitle}>
                Floor {floor} ({roomsByFloor[floor].length} rooms)
              </div>

              <div style={styles.roomsGrid}>
                {roomsByFloor[floor].map(room => (
                  <div
                    key={room.id}
                    style={{
                      ...styles.roomCard,
                      background: getStatusColor(room.status),
                      borderColor: getStatusColor(room.status),
                      opacity: room.status === 'maintenance' ? 0.6 : 1
                    }}
                    onClick={() => room.status !== 'maintenance' && handleRoomClick(room)}
                    onMouseEnter={(e) => {
                      if (!isMobile && room.status !== 'maintenance') {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <div style={styles.roomNumber}>{room.room_number}</div>
                    <div style={styles.roomType}>{room.room_type}</div>
                    {room.guest_name && (
                      <div style={{
                        ...styles.roomType,
                        marginTop: '4px',
                        fontSize: isMobile ? '8px' : '9px'
                      }}>
                        {room.guest_name.split(' ')[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.contentCard}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.guestTable}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Room</th>
                  {!isMobile && <th style={styles.tableHeader}>Type</th>}
                  <th style={styles.tableHeader}>Guest</th>
                  {!isMobile && <th style={styles.tableHeader}>Phone</th>}
                  <th style={styles.tableHeader}>Check-out</th>
                  <th style={styles.tableHeader}>Payment</th>
                  <th style={styles.tableHeader}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentGuests.map(guest => (
                  <tr key={guest.id}>
                    <td style={styles.tableCell}>
                      <strong>{guest.room_number}</strong>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{guest.booking_type}</td>
                    )}
                    <td style={styles.tableCell}>
                      {guest.guest_name}
                      {isMobile && <div style={{ fontSize: '11px', color: colors.textLight }}>
                        {guest.booking_type}
                      </div>}
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{guest.guest_phone}</td>
                    )}
                    <td style={styles.tableCell}>
                      {new Date(guest.check_out_date).toLocaleDateString()}
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.badge,
                        ...(guest.payment_status === 'completed' ? styles.successBadge :
                          guest.payment_status === 'partial' ? styles.warningBadge :
                            styles.dangerBadge)
                      }}>
                        {guest.payment_status}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleCheckout(guest)}
                        style={{
                          ...styles.button,
                          ...styles.dangerButton,
                          padding: isMobile ? '6px 12px' : '8px 16px',
                          fontSize: isMobile ? '11px' : '12px',
                          width: 'auto'
                        }}
                      >
                        Checkout
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Guest Details Modal */}
      {showGuestDetails && selectedRoom && (
        <div style={styles.modal} onClick={() => setShowGuestDetails(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                Room {selectedRoom.room_number} - Guest Details
              </h3>
              <button
                style={styles.closeButton}
                onClick={() => setShowGuestDetails(false)}
              >
                ×
              </button>
            </div>

            <div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Guest Name:</span>
                <span style={styles.detailValue}>{selectedRoom.guest_name}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Email:</span>
                <span style={styles.detailValue}>{selectedRoom.guest_email || 'N/A'}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Phone:</span>
                <span style={styles.detailValue}>{selectedRoom.guest_phone}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Nationality:</span>
                <span style={styles.detailValue}>
                  {selectedRoom.guest_nationality === 'Indian' ? '🇮🇳 Indian' : '🌍 Foreign'}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Booking Type:</span>
                <span style={styles.detailValue}>{selectedRoom.booking_type}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Number of Guests:</span>
                <span style={styles.detailValue}>{selectedRoom.number_of_guests}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>ID Proof:</span>
                <span style={styles.detailValue}>{selectedRoom.guest_id_proof || 'N/A'}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Check-in Date:</span>
                <span style={styles.detailValue}>
                  {new Date(selectedRoom.check_in_date).toLocaleDateString()}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Check-out Date:</span>
                <span style={styles.detailValue}>
                  {new Date(selectedRoom.check_out_date).toLocaleDateString()}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Room Type:</span>
                <span style={styles.detailValue}>{selectedRoom.room_type}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Total Amount:</span>
                <span style={styles.detailValue}>
                  ₹{parseFloat(selectedRoom.total_amount).toLocaleString('en-IN')}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Payment Status:</span>
                <span style={{
                  ...styles.detailValue,
                  ...styles.badge,
                  ...(selectedRoom.payment_status === 'completed' ? styles.successBadge :
                    selectedRoom.payment_status === 'partial' ? styles.warningBadge :
                      styles.dangerBadge)
                }}>
                  {selectedRoom.payment_status}
                </span>
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={() => setShowGuestDetails(false)}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Guest Modal */}
      {showAssignForm && selectedRoom && (
        <div style={styles.modal} onClick={() => setShowAssignForm(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                Assign Guest to Room {selectedRoom.room_number}
              </h3>
              <button
                style={styles.closeButton}
                onClick={() => setShowAssignForm(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAssignGuest}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Guest Name *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={guestForm.guestName}
                  onChange={(e) => setGuestForm({ ...guestForm, guestName: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  style={styles.input}
                  value={guestForm.guestEmail}
                  onChange={(e) => setGuestForm({ ...guestForm, guestEmail: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  style={styles.input}
                  value={guestForm.guestPhone}
                  onChange={(e) => setGuestForm({ ...guestForm, guestPhone: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Nationality *</label>
                <select
                  style={styles.select}
                  value={guestForm.guestNationality}
                  onChange={(e) => setGuestForm({ ...guestForm, guestNationality: e.target.value })}
                >
                  <option value="Indian">🇮🇳 Indian</option>
                  <option value="Foreign">🌍 Foreign</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Booking Type *</label>
                <select
                  style={styles.select}
                  value={guestForm.bookingType}
                  onChange={(e) => setGuestForm({ ...guestForm, bookingType: e.target.value })}
                >
                  <option value="Single">Single</option>
                  <option value="Couple">Couple</option>
                  <option value="Unmarried Couple">Unmarried Couple</option>
                  <option value="Family/Friends">Family/Friends</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Number of Guests *</label>
                <input
                  type="number"
                  style={styles.input}
                  min="1"
                  value={guestForm.numberOfGuests}
                  onChange={(e) => setGuestForm({ ...guestForm, numberOfGuests: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>ID Proof</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g., AADHAAR-1234-5678-9012"
                  value={guestForm.guestIdProof}
                  onChange={(e) => setGuestForm({ ...guestForm, guestIdProof: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Check-in Date *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={guestForm.checkInDate}
                  onChange={(e) => setGuestForm({ ...guestForm, checkInDate: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Check-out Date *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={guestForm.checkOutDate}
                  onChange={(e) => setGuestForm({ ...guestForm, checkOutDate: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Total Amount (₹) *</label>
                <input
                  type="number"
                  style={styles.input}
                  min="0"
                  step="0.01"
                  value={guestForm.totalAmount}
                  onChange={(e) => setGuestForm({ ...guestForm, totalAmount: e.target.value })}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...styles.primaryButton
                  }}
                >
                  Assign Guest
                </button>
                <button
                  type="button"
                  onClick={() => setShowAssignForm(false)}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Checkout Confirmation Modal */}
      {showCheckoutConfirm && checkoutGuest && (
        <div style={styles.modal} onClick={() => setShowCheckoutConfirm(false)}>
          <div style={{
            ...styles.modalContent,
            maxWidth: isMobile ? '100%' : '400px'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Confirm Checkout</h3>
              <button
                style={styles.closeButton}
                onClick={() => setShowCheckoutConfirm(false)}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '12px' }}>
                Are you sure you want to checkout <strong>{checkoutGuest.guest_name}</strong> from room <strong>{checkoutGuest.room_number}</strong>?
              </p>

              <div style={{
                ...styles.detailRow,
                flexDirection: 'column',
                gap: '8px',
                padding: '12px',
                background: checkoutGuest.payment_status === 'completed'
                  ? 'rgba(74, 124, 42, 0.1)'
                  : 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>
                  Total Amount: ₹{parseFloat(checkoutGuest.total_amount).toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>
                  Paid: ₹{parseFloat(checkoutGuest.total_paid || 0).toLocaleString('en-IN')}
                </div>
                {checkoutGuest.pending_amount > 0 && (
                  <div style={{ fontSize: '14px', fontWeight: '700', color: colors.danger }}>
                    Pending: ₹{parseFloat(checkoutGuest.pending_amount).toLocaleString('en-IN')}
                  </div>
                )}
              </div>

              {checkoutGuest.pending_amount > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: colors.danger,
                  fontWeight: '600'
                }}>
                  ⚠️ Payment pending! Please clear all payments before checkout.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={confirmCheckout}
                disabled={checkoutGuest.pending_amount > 0}
                style={{
                  ...styles.button,
                  ...styles.dangerButton,
                  opacity: checkoutGuest.pending_amount > 0 ? 0.5 : 1,
                  cursor: checkoutGuest.pending_amount > 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {checkoutGuest.pending_amount > 0 ? 'Clear Payment First' : 'Confirm Checkout'}
              </button>
              <button
                onClick={() => setShowCheckoutConfirm(false)}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default StayOverview;